import { mixed } from '@aninix-inc/model/legacy'
import InputAdornment from '@material-ui/core/InputAdornment'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import { default as classnames } from 'classnames'
import * as R from 'ramda'
import * as React from 'react'

import { OnboardingObject } from '@aninix/app-design-system/hooks/use-onboarding'
import { defaultFigmaLikeFormat } from '@aninix/app-design-system/utils/figma-like-number-format'
import { MathOpsMixedGuide } from './math-ops-mixed-guide'

import useKeyModifiers from '@aninix/app-design-system/hooks/use-key-modifiers'
import { OnboardingPopover } from '../onboarding-popover'
import * as styles from './index.scss'
import { MathOpsNumberGuide } from './math-ops-number-guide'
import { getKeysFromKeyboardEvent } from './utils/get-keys-from-keyboard-event'
import { getNumberInputValue } from './utils/get-number-input-value'
import { getValueAfterDeltaChange } from './utils/get-value-after-delta-change'
import { getValueAfterKeyPress } from './utils/get-value-after-key-press'
import { getValueFromInput } from './utils/get-value-from-input'
import { isRelativeMathOps } from './utils/is-relative-math-ops'

export interface IProps {
  id: string
  value: number | typeof mixed
  /**
   * @description offset modifier, less value make scroll more precisable
   */
  threshold?: number
  onStartChange?: () => void
  // @TODO: it's called twice, have to fix
  onChange?: (value: number) => void
  onEndChange?: () => void
  onDeltaChange?: (delta: number) => void
  min?: number
  max?: number
  disabled?: boolean
  dragDisabled?: boolean
  icon?: React.ReactNode
  format?: (value: number) => string
  width?: number
  /**
   * @description if provided it will override .iconWide property
   */
  iconWidth?: number
  iconWide?: boolean
  formatBeforeApply?: (value: string) => string
}
const returnValue = (value: string) => value
const noop = () => {}

export const Input: React.FCC<IProps> = ({
  id,
  value,
  threshold = 1,
  onStartChange = noop,
  onChange = noop,
  onEndChange = noop,
  onDeltaChange,
  min = -Infinity,
  max = Infinity,
  disabled = false,
  dragDisabled = false,
  icon,
  format = defaultFigmaLikeFormat,
  width = 78,
  iconWidth,
  iconWide = false,
  formatBeforeApply = returnValue,
}) => {
  const [isMoving, setIsMoving] = React.useState(false)
  const inputWrapperRef = React.createRef<HTMLInputElement>()
  const inputRef = React.createRef<HTMLInputElement>()
  const onbMixedRef = React.useRef<OnboardingObject>(null)
  const onbNumberRef = React.useRef<OnboardingObject>(null)

  const { isCtrlDown, isShiftDown, isAltDown } = useKeyModifiers()
  const [isFocused, setIsFocused] = React.useState(false)

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const {
      isKeyDown,
      isKeyUp,
      isShiftPressed,
      isCtrlPressed,
      isEnter,
      isEsc,
    } = getKeysFromKeyboardEvent(e)

    if (value !== mixed && (isKeyDown || isKeyUp)) {
      e.preventDefault()
      e.stopPropagation()

      const valueToApply = getValueAfterKeyPress({
        threshold,
        isShiftPressed,
        isCtrlPressed,
        value,
        isPositive: isKeyUp,
      })

      onChange(R.clamp(min, max, valueToApply))
    }

    if (isEnter || isEsc) {
      inputRef.current?.blur()
    }
  }

  const handleFocus = () => {
    setIsFocused(true)

    onStartChange()
  }
  const handleBlur: React.FocusEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    setIsFocused(false)

    const newValue = getValueFromInput({
      formatBeforeApply,
      inputValue: e.currentTarget.value,
      initialValue: value,
      min,
      max,
    })

    if (typeof newValue !== 'number' || isNaN(newValue)) return

    if (value === mixed && isRelativeMathOps(e.currentTarget.value)) {
      onDeltaChange?.(R.clamp(min, max, newValue))
    } else {
      onChange(R.clamp(min, max, newValue))
    }

    onEndChange()
  }

  const inputValue = getNumberInputValue({ value, format, isFocused })

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> =
    React.useCallback(
      (e) => {
        if (!isMoving) return

        const delta = getValueAfterDeltaChange({
          isCtrlDown,
          isShiftDown,
          deltaX: e.movementX,
          threshold,
        })

        if (value !== mixed) {
          const nextValue = value + delta
          const newDelta = nextValue >= min && nextValue <= max ? delta : 0
          onDeltaChange?.(newDelta)
        } else {
          onDeltaChange?.(delta)
        }
      },
      [isMoving, isCtrlDown, isShiftDown, threshold, value]
    )

  React.useEffect(() => {
    if (inputRef.current == null) return

    inputRef.current.value = inputValue

    if (isFocused) inputRef.current?.select()
  }, [value, isFocused])

  return (
    <>
      <OnboardingPopover
        anchorEl={inputWrapperRef.current}
        name="math-ops-mixed"
        ref={onbMixedRef}
      >
        <MathOpsMixedGuide
          handleClose={() => onbMixedRef.current!.close()}
          handlePass={() => onbMixedRef.current!.pass()}
        />
      </OnboardingPopover>

      <OnboardingPopover
        anchorEl={inputWrapperRef.current}
        name="math-ops-number"
        ref={onbNumberRef}
      >
        <MathOpsNumberGuide
          handleClose={() => onbNumberRef.current!.close()}
          handlePass={() => onbNumberRef.current!.pass()}
          exampleValue={value === mixed ? 0 : value}
        />
      </OnboardingPopover>

      <OutlinedInput
        ref={inputWrapperRef}
        inputRef={inputRef}
        inputProps={{ style: { height: '100%' } }}
        classes={{
          adornedStart: classnames(styles['input-root'], {
            [styles['input--alt-down']]: isAltDown,
          }),
          root: classnames(styles['input-root'], {
            [styles.focused]: isFocused || isMoving,
            [styles['input--alt-down']]: isAltDown,
          }),
          notchedOutline: classnames(styles['outline-notched'], {}),
          input: classnames(styles.input, {
            [styles['input--mixed']]:
              isFocused === false && isMoving === false && value === mixed,
            [styles['input--disabled']]: disabled,
            [styles['input--no-icon']]: icon == null,
            [styles['input--alt-down']]: isAltDown,
          }),
          focused: styles.focused,
          disabled: styles.disabled,
        }}
        style={{ width, pointerEvents: isMoving ? 'none' : 'auto' }}
        id={id}
        type="text"
        defaultValue={inputValue}
        onKeyDown={handleKeyPress}
        onPointerMove={handlePointerMove}
        onPointerDown={(e) => {
          if (disabled || dragDisabled) return
          setIsMoving(true)
          e.currentTarget.setPointerCapture(e.pointerId)
        }}
        onPointerUp={(e) => {
          setIsMoving(false)
          e.currentTarget.releasePointerCapture(e.pointerId)
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        autoComplete="off"
        startAdornment={
          icon != null && (
            <InputAdornment
              classes={{
                root: classnames(styles['input-adornment'], {
                  [styles['input-adornment--disabled']]:
                    dragDisabled && !isAltDown,
                }),
              }}
              position="start"
            >
              <span
                className={classnames(styles.icon, {
                  [styles['icon--wide']]: iconWide,
                })}
                style={{ width: iconWidth }}
              >
                {icon}
              </span>
            </InputAdornment>
          )
        }
        disabled={disabled}
      />
    </>
  )
}
